import * as Yup from 'yup';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import {
  ButtonActor,
  DigitalCodeInputActor,
  Link,
  Text,
  Title,
} from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from 'utils/translations';
import { JUICY_NEXT_BTN } from 'utils/constants';
import { PHONE, PHONE_LINK } from 'utils/constants';
import { useFormik } from 'formik';
import { usePhoneVerificationScreenLogic } from './usePhoneVerificationScreenLogic';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import layout from 'scss/layout.module.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import RegistrationLoading from '../RegistrationLoading/RegistrationLoading';
import styles from './PhoneVerification.module.scss';
import useClient from '../../hooks/useClient';

export interface PhoneVerificationForm {
  verificationCode0?: string;
  verificationCode1?: string;
  verificationCode2?: string;
  verificationCode3?: string;
}

const phoneVerificationValidationSchema = Yup.object().shape({
  verificationCode0: Yup.string().required(),
  verificationCode1: Yup.string().required(),
  verificationCode2: Yup.string().required(),
  verificationCode3: Yup.string().required(),
});

export default function PhoneVerification() {
  const i18Next = useTranslation('phoneVerification');
  const [showError, setShowError] = useState(false);
  const [loadingVerification, setLoadingVerification] = useState(false);
  const [isTimeoutDisplayed, setIsTimeoutDisplayed] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [isPromisePending, setIsPromisePending] = useState(false);
  const {
    change_phone_text_1,
    handleSubmit,
    loading,
    resendCode,
    navigateToChangePhone,
  } = usePhoneVerificationScreenLogic();
  const { fetchClient } = useClient();

  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const formik = useFormik<PhoneVerificationForm>({
    initialValues: {
      verificationCode0: undefined,
      verificationCode1: undefined,
      verificationCode2: undefined,
      verificationCode3: undefined,
    },
    validationSchema: phoneVerificationValidationSchema,
    onSubmit: async (data) => {
      setLoadingVerification(true);
      await handleSubmit(data);
      await fetchClient();
      setLoadingVerification(false);
    },
  });

  const continueBtnClick = async () => {
    const isFormValid = Object.keys(await formik.validateForm()).length === 0;
    if (!isFormValid) {
      setShowError(true);
    }
  };

  useEffect(() => {
    countdownIntervalRef.current = setInterval(
      () => isTimeoutDisplayed && setTimeLeft(timeLeft - 1),
      1000,
    );

    return () => {
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
    };
  }, [isTimeoutDisplayed, timeLeft]);

  /* Logic needs to be implemented once we have some data from BE about validation.
  At this point you need to enter any 2+ digits to be allowed further */

  const formatTimeLeft = timeLeft > 9 ? timeLeft : '0' + timeLeft;

  const handlePendingSms = useCallback(async () => {
    try {
      setIsPromisePending(true);
      const timeOutDisplayed = await resendCode();
      setIsPromisePending(false);
      setIsTimeoutDisplayed((prev) => !prev);
      setTimeLeft(timeOutDisplayed || 60);
      setTimeout(
        () => {
          setIsTimeoutDisplayed((prev) => !prev);
        },
        timeOutDisplayed ? timeOutDisplayed * 1000 : 60000,
      );
    } catch {
      setIsPromisePending(false);
    }
  }, [resendCode]);

  // Initial call to send the sms code
  useEffect(() => {
    const fn = async () => handlePendingSms();
    fn();
  }, []);

  const onChangeDigitalInput = () => {
    if (showError) {
      setShowError(false);
    }
  };

  if (loading) return <RegistrationLoading />;

  const content = getScreenTranslation('phoneVerification', i18Next);
  const classNameDigitalCode = showError ? styles['border-error'] : '';
  const borderRadius = showError ? '' : styles.border;

  return (
    <Frame
      {...frameConfig}
      sectionID={AppRegistrationSection.Confirmation}
      hideEditButton
    >
      <form onSubmit={formik.handleSubmit}>
        <Title
          text={content.heading}
          data-test="css-Phone-Verification-Title"
        />
        <div
          className={classes(styles.subheading)}
          data-test="css-Phone-Verification-Subtitle"
        >
          <Text text={content.subheading} weight="bold" level={2} />
        </div>
        <DigitalCodeInputActor
          classNameFieldGroup={classes(styles['field-group'], borderRadius)}
          className={classNameDigitalCode}
          formik={formik}
          onChange={onChangeDigitalInput}
          name="verificationCode"
          data-test="css-Phone-Verification-Code"
          analytics={{
            description: 'Verification code',
          }}
        />
        {showError && (
          <Text
            className={styles['error-text']}
            level={3}
            text={content.error_message}
          />
        )}
        <div className={classes(styles['resend-code'])}>
          {isTimeoutDisplayed && !isPromisePending && (
            <Text
              text={content.time_remaining + ' 00:' + formatTimeLeft}
              weight="regular"
              level={2}
            />
          )}
          {isPromisePending && (
            <Text
              text={content.resend_code}
              weight="medium"
              level={1}
              className={classes(styles['resend-code-text-pending'])}
            />
          )}
          {!isTimeoutDisplayed && !isPromisePending && (
            <Link onClick={handlePendingSms} dataTest="xpath-Resend-Code">
              <Text
                text={content.resend_code}
                weight="medium"
                level={1}
                className={classes(styles['resend-code-text'])}
              />
            </Link>
          )}
        </div>
        <div className={classes(styles['phone-check'])} data-test="css-Info">
          <Text
            className={classes(styles['phone-check'])}
            weight="regular"
            level={3}
            dangerouslySetInnerHTML={{
              __html: i18Next.t('change_phone_text_1', {
                clientPhone: change_phone_text_1,
              }),
            }}
          />

          <Link onClick={navigateToChangePhone} className={styles.link}>
            {content.change_phone_link_text}
          </Link>
          <Text
            className={classes(styles['phone-check'])}
            weight="regular"
            level={3}
            dangerouslySetInnerHTML={{
              __html: i18Next.t('change_phone_text_2', {
                phone: PHONE,
                'phone-no-spaces': PHONE_LINK,
              }),
            }}
          />

          <Text
            className={classes(styles['phone-check'])}
            weight="regular"
            level={3}
            dangerouslySetInnerHTML={{
              __html: content.spam_folder,
            }}
            dataTest="phoneVerification-spam-folder"
          />
        </div>
        <ButtonActor
          buttonProps={{
            className: classes([
              styles.button,
              layout['width-sm'],
              JUICY_NEXT_BTN,
            ]),
            dataTest: 'css-Continue-Button',
            loading: loadingVerification,
            onClick: continueBtnClick,
            type: 'submit',
          }}
          formik={formik}
        >
          {content.submit_button}
        </ButtonActor>
      </form>
    </Frame>
  );
}
